import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
} from '@mui/material';

import CustomerPicker from './CustomerPicker';
import ResponsiveSheet, { useSheetFullScreen } from './ResponsiveSheet';
import SheetHeader from './SheetHeader';
import { TOKENS } from '../theme';

// Spec §5 — Raffle edit/add sheet.
//
// Both EDIT and ADD modes use the same customer picker so manager workflow
// is consistent with the employee RaffleService.save flow.
//
// EDIT mode: customer/amount/winner/time pre-filled from the entry; both
//   stored ID photo and validation photo shown read-only.
// ADD mode: blank pickers; no photos captured (manager not present at
//   the counter); audit log marks the entry as a late entry.
export default function RaffleEditSheet({
  open,
  onClose,
  onSave,
  mode = 'edit',
  entry = {},
  index,
  customers = [],
}) {
  const isAdd = mode === 'add';

  // Pre-fill customer from the entry's customerId.
  const initialCustomer = useMemo(
    () => customers?.find((c) => c.customerId === entry.customerId) || null,
    [customers, entry.customerId]
  );

  const [selectedCustomer, setSelectedCustomer] = useState(initialCustomer);
  const [amount, setAmount] = useState(entry.amount || '');
  const [winnerNumber, setWinnerNumber] = useState(entry.winnerNumber || '');
  const [time, setTime] = useState(entry.time || '');

  useEffect(() => {
    if (initialCustomer) setSelectedCustomer(initialCustomer);
  }, [initialCustomer]);

  const canSave = Boolean(selectedCustomer && amount);

  const handleSave = () => {
    if (!canSave) return;
    const name = `${selectedCustomer.firstName || ''} ${
      selectedCustomer.lastName || ''
    }`.trim();
    onSave({
      customerId: selectedCustomer.customerId,
      name,
      amount: String(amount),
      winnerNumber: String(winnerNumber || ''),
      time: time || undefined,
    });
  };

  const idPhotoUrl = entry?.photo?.[0]?.localURL;
  const validationPhotoUrl = entry?.files?.[0]?.localURL;
  const isPage = useSheetFullScreen();

  return (
    <ResponsiveSheet open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: isPage ? '100%' : undefined,
          maxHeight: isPage ? '100%' : 'inherit',
        }}
      >
        <SheetHeader
          eyebrow="Raffle"
          eyebrowColor={TOKENS.pink700}
          title={isAdd ? 'Add Raffle Entry' : `Edit Raffle #${index ?? '—'}`}
          onClose={onClose}
        />
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <CustomerPicker
            value={selectedCustomer}
            onChange={setSelectedCustomer}
            customers={customers}
          />
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Field label="Amount $*">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Field>
            </Grid>
            <Grid item xs={6}>
              <Field label="Winner Number">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={winnerNumber}
                  onChange={(e) => setWinnerNumber(e.target.value)}
                />
              </Field>
            </Grid>
          </Grid>
          {!isAdd && (
            <Field label="Time">
              <TextField
                fullWidth
                size="small"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                placeholder="HH:MM AM/PM"
              />
            </Field>
          )}
          {!isAdd && (idPhotoUrl || validationPhotoUrl) && (
            <Field label="Photos (read-only — captured at counter)">
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <PhotoPreview
                  src={idPhotoUrl}
                  label="ID Picture"
                  borderColor={TOKENS.blue200}
                  chipBg={TOKENS.blue50}
                  chipText={TOKENS.blue700}
                />
                <PhotoPreview
                  src={validationPhotoUrl}
                  label="Validation"
                  borderColor={TOKENS.emerald200}
                  chipBg={TOKENS.emerald50}
                  chipText={TOKENS.emerald700}
                />
              </Grid>
            </Field>
          )}
          {isAdd && (
            <Box
              sx={{
                p: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: TOKENS.amber200,
                background: TOKENS.amber50,
              }}
            >
              <Typography sx={{ fontSize: 11, color: TOKENS.amber900 }}>
                Photo capture is skipped — manager not present when raffle was
                drawn. The audit log records this as a late entry.
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderTop: `1px solid ${TOKENS.slate200}`,
            display: 'flex',
            gap: 1,
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            background: '#fff',
            pb: isPage ? 'calc(12px + env(safe-area-inset-bottom))' : 1.5,
          }}
        >
          <Button fullWidth variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={!canSave}
            onClick={handleSave}
            sx={{
              background: TOKENS.pink600,
              '&:hover': { background: TOKENS.pink700 },
            }}
          >
            {isAdd ? 'Add Entry' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </ResponsiveSheet>
  );
}

function Field({ label, children }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.slate500,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

function PhotoPreview({ src, label, borderColor, chipBg, chipText }) {
  return (
    <Grid item xs={6}>
      <Box
        sx={{
          border: `2px solid ${borderColor}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ background: chipBg, px: 1, py: 0.5 }}>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: chipText,
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ aspectRatio: '3/4', background: TOKENS.slate100 }}>
          {src && (
            <Box
              component="img"
              src={src}
              alt={label}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          )}
        </Box>
      </Box>
    </Grid>
  );
}
